/**
 * Kingdom Factor - Share Your Story Landing Page
 * - Hero with headline, subtext, CTA, and professional photo
 * - What Is the KF Collective Interview? (3-column cards with red left-border)
 * - Why Christian Business Owners Participate (4-column with stock photos)
 * - The Interview Process (5 numbered steps)
 * - What to Expect (4 info cards on pink/rose background)
 * - Apply to Be Featured (inline form with sidebar)
 * - Final CTA with blockquote
 */

import { useForm, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { useState, useEffect, useRef } from "react"
import { ArrowRight, Upload, MapPin, Clock, ClipboardList, Camera, CheckCircle2, MessageSquare } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { formatPhone, isValidEmail } from "@/lib/form-utils"
import { US_STATES } from "@/lib/us-states"
import { COUNTRIES } from "@/lib/countries"

const R2_BASE_URL = "https://pub-d0ab0b104ce14000869f88e713e41bb8.r2.dev"

const collectiveCards = [
  {
    title: "Your Journey",
    description:
      "Each featured leader participates in a short, relaxed interview where we explore their business journey and how faith has shaped who they are as a leader.",
  },
  {
    title: "Your Story, Published",
    description:
      "Your conversation is then transformed into a beautifully written article, published to inspire and equip other Christian leaders navigating faith in the marketplace.",
  },
  {
    title: "A Wider Reach",
    description:
      "Your published story reaches a growing community of faith-driven business owners and entrepreneurs who are hungry for real, honest, Kingdom-minded leadership examples.",
  },
]

const whyParticipate = [
  {
    image: `${R2_BASE_URL}/marketing_assets/collective_encourage_leaders.webp`,
    title: "Encourage Other Leaders",
    description:
      "Your journey \u2014 the challenges, the pivots, the faith-filled decisions \u2014 can inspire other entrepreneurs who are navigating how to lead faithfully in business.",
  },
  {
    image: `${R2_BASE_URL}/marketing_assets/collective_kingdom_impact.webp`,
    title: "Share Your Kingdom Impact",
    description:
      "Highlight how your business reflects biblical principles and serves people beyond profit. Your example shows others that faith and business belong together.",
  },
  {
    image: `${R2_BASE_URL}/marketing_assets/collective_gain_visibility.webp`,
    title: "Gain Visibility for Your Business",
    description:
      "Your interview becomes a featured article that introduces your company and your mission to a network of like-minded, faith-driven leaders and entrepreneurs.",
  },
  {
    image: `${R2_BASE_URL}/marketing_assets/collective_simple_process.webp`,
    title: "Simple and Relaxed Process",
    description:
      "The interview is casual, conversational, and designed to make sharing your story easy. No scripts, no pressure \u2014 just an honest conversation about your faith and your work.",
  },
]

const interviewSteps = [
  {
    number: "01",
    title: "Submit Your Application",
    description:
      "Complete a short application to share a little about you, your business, and how your faith shapes your leadership.",
  },
  {
    number: "02",
    title: "Introduction Call",
    description:
      "We schedule a brief 15\u201320 minute call to get to know you better and walk you through the interview process so you feel fully prepared.",
  },
  {
    number: "03",
    title: "Your Recorded Interview",
    description:
      "We schedule your recorded interview \u2014 typically 20\u201330 minutes \u2014 at a time that works for your schedule. It\u2019s relaxed and conversational.",
  },
  {
    number: "04",
    title: "Five Key Areas of Discussion",
    description:
      "During the interview, we explore: your business and leadership journey, how your faith influences your work, decisions guided by biblical values, generosity and stewardship in your business, and encouragement for other Christian leaders.",
  },
  {
    number: "05",
    title: "Your Story Gets Published",
    description:
      "Our team transforms your interview into a polished written article and publishes it on the Kingdom Factor Collective platform, where it reaches leaders across the country.",
  },
]

const expectCards = [
  {
    icon: MapPin,
    title: "Format",
    description: (
      <>
        Remote interview conducted via <strong>Zoom or Google Meet</strong> &mdash; from the comfort of your home or office.
      </>
    ),
  },
  {
    icon: Clock,
    title: "Length",
    description: (
      <>
        Interviews are typically <strong>20&ndash;30 minutes</strong> &mdash; short enough to respect your schedule, meaningful enough to tell your story well.
      </>
    ),
  },
  {
    icon: ClipboardList,
    title: "Preparation",
    description: (
      <>
        <strong>No preparation required.</strong> We guide the entire conversation. Just show up as yourself &mdash; that&apos;s more than enough.
      </>
    ),
  },
  {
    icon: Camera,
    title: "Photo",
    description: (
      <>
        We may request a <strong>headshot</strong> for your article, or we can use a photo from your LinkedIn profile or company website.
      </>
    ),
  },
]

export default function ShareYourStory({ seo, settings = {} }) {
  const { scheduleCallUrl = "#" } = settings
  const { flash } = usePage().props
  const formRef = useRef(null)
  const submitted = flash?.notice === "submitted"

  const [clientErrors, setClientErrors] = useState({})
  const [headshotPreview, setHeadshotPreview] = useState(null)

  const form = useForm({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company_name: "",
    website: "",
    title_role: "",
    linkedin: "",
    city: "",
    state: "",
    country: "US",
    faith_reflection: "",
    headshot: null,
    website_url: "", // honeypot
  })

  // Scroll to #apply on page load if hash is present
  useEffect(() => {
    if (window.location.hash === "#apply") {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth" })
      }, 100)
    }
  }, [])

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const validate = () => {
    const errors = {}

    if (!form.data.first_name.trim()) errors.first_name = "First name is required"
    if (!form.data.last_name.trim()) errors.last_name = "Last name is required"
    if (!form.data.email.trim()) {
      errors.email = "Email is required"
    } else if (!isValidEmail(form.data.email)) {
      errors.email = "Please enter a valid email address"
    }
    if (!form.data.phone.trim()) errors.phone = "Phone number is required"
    if (!form.data.company_name.trim()) errors.company_name = "Company name is required"
    if (!form.data.title_role.trim()) errors.title_role = "Title/role is required"
    if (!form.data.city.trim()) errors.city = "City is required"
    if (form.data.country === "US" && !form.data.state) errors.state = "Please select a state"
    if (!form.data.faith_reflection.trim()) {
      errors.faith_reflection = "This field is required"
    } else if (form.data.faith_reflection.trim().length < 50) {
      errors.faith_reflection = "Please share at least a few sentences (50 characters minimum)"
    }

    if (form.data.headshot) {
      const file = form.data.headshot
      const validTypes = ["image/jpeg", "image/png", "image/webp"]
      if (!validTypes.includes(file.type)) {
        errors.headshot = "Please upload a JPEG, PNG, or WebP image"
      } else if (file.size > 5 * 1024 * 1024) {
        errors.headshot = "Image must be less than 5MB"
      }
    }

    setClientErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    form.post("/share-your-story", {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  const setField = (field, value) => {
    form.setData(field, value)
    if (clientErrors[field]) {
      setClientErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    setField("phone", formatted)
  }

  const handleHeadshotChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      if (headshotPreview) URL.revokeObjectURL(headshotPreview)
      setField("headshot", file)
      setHeadshotPreview(URL.createObjectURL(file))
    }
  }

  const errorFor = (field) => clientErrors[field] || form.errors?.[field]?.[0]

  return (
    <>
      <SeoHead seo={seo} />
      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="share-your-story" scheduleCallUrl={scheduleCallUrl} />

        {/* Section 1: Hero */}
        <section className="pt-32 pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-kf-red leading-tight">
                  Share Your Story. Strengthen the Kingdom in the Marketplace.
                </h1>
                <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl">
                  The Kingdom Factor Collective features Christian business leaders and how they live out their faith in business. Through a short, conversational interview, your story becomes a published article that encourages other leaders to integrate faith and business.
                </p>
                <Button variant="gradient" size="lg" className="mt-8" onClick={scrollToForm}>
                  Apply to Be Featured
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="mt-6 text-sm italic text-zinc-500 max-w-lg">
                  Featured stories are shared with a growing network of Christian business leaders and entrepreneurs seeking to lead with faith and purpose.
                </p>
              </div>
              <div className="hidden lg:flex justify-end">
                <img
                  src={`${R2_BASE_URL}/marketing_assets/collective_hero.webp`}
                  alt="Professional Christian business leader"
                  className="max-h-[550px] object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: What Is the Kingdom Factor Collective Interview? */}
        <section className="py-24 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              What is the Kingdom Factor Collective Interview?
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-4xl">
              The Kingdom Factor Collective highlights how Christian business owners and leaders integrate their faith into leadership, decision-making, and culture inside their businesses. We believe every entrepreneur has a story worth telling &mdash; and that story has the power to strengthen the Body of Christ in the marketplace.
            </p>
            <div className="mt-12 grid md:grid-cols-3 gap-8">
              {collectiveCards.map((card) => (
                <div
                  key={card.title}
                  className="bg-white rounded-lg border border-zinc-200 p-8 border-l-4 border-l-kf-red"
                >
                  <h3 className="font-display text-xl font-semibold text-zinc-900">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-zinc-600 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Why Christian Business Owners Participate */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              Why Christian Business Owners Participate
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-4xl">
              Sharing your story isn&apos;t just about you &mdash; it&apos;s about the leader who needs to hear it. Here&apos;s why Kingdom-minded business owners choose to be featured in the Collective.
            </p>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyParticipate.map((item) => (
                <div key={item.title}>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-5">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-zinc-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-zinc-600 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: The Interview Process */}
        <section className="py-24 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              The Interview Process
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-4xl">
              We&apos;ve designed every step to be simple, straightforward, and respectful of your time. Here&apos;s exactly what you can expect from start to published article.
            </p>
            <div className="mt-12 grid md:grid-cols-3 gap-x-8 gap-y-12">
              {interviewSteps.map((step) => (
                <div key={step.number}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm font-mono text-zinc-400">{step.number}</span>
                    <div className="flex-1 h-px bg-kf-red" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-zinc-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-zinc-600 leading-relaxed text-sm">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5: What to Expect */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              What to Expect
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-4xl">
              The interview is designed to feel like a conversation between friends &mdash; not a formal sit-down or a high-pressure performance. There are no perfect answers, no scripted responses required. We simply want to hear <em>your</em> story, in your own words.
            </p>
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {expectCards.map((card) => {
                const Icon = card.icon
                return (
                  <div
                    key={card.title}
                    className="bg-rose-50 rounded-lg p-8"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="h-5 w-5 text-kf-red" />
                      <h3 className="font-display text-lg font-semibold text-zinc-900">
                        {card.title}
                      </h3>
                    </div>
                    <p className="text-zinc-600 leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Section 6: Apply to Be Featured (Form) */}
        <section id="apply" ref={formRef} className="py-24 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              Apply to Be Featured
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-4xl">
              If you are a Christian business owner or leader who desires to encourage others through your story, we would love to hear from you. Complete the application below and our team will follow up to schedule your introduction call &mdash; usually within a few business days.
            </p>

            {submitted ? (
              <div className="mt-12 bg-white rounded-lg border border-zinc-200 p-12 text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-display text-2xl text-zinc-900">
                  Application Received!
                </h3>
                <p className="mt-4 text-zinc-600 leading-relaxed">
                  Thank you for applying to be featured in the Kingdom Factor Collective. Our team reviews every application personally and we&apos;ll be in touch within a few business days to schedule your introduction call.
                </p>
              </div>
            ) : (
              <div className="mt-12 grid lg:grid-cols-3 gap-8">
                {/* Form */}
                <div className="lg:col-span-2">
                  <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-zinc-200 p-8">
                    {/* Honeypot — hidden from real users, bots auto-fill it */}
                    <div className="absolute overflow-hidden" style={{ width: 0, height: 0, opacity: 0 }} aria-hidden="true" tabIndex={-1}>
                      <label htmlFor="website_url">Website URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={form.data.website_url}
                        onChange={(e) => form.setData("website_url", e.target.value)}
                        autoComplete="off"
                        tabIndex={-1}
                      />
                    </div>

                    {/* Base-level errors (rate limiting, etc.) */}
                    {(errorFor("base") || form.errors?.base?.[0]) && (
                      <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                        {errorFor("base") || form.errors?.base?.[0]}
                      </div>
                    )}

                    {/* Your Information */}
                    <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">
                      Your Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          value={form.data.first_name}
                          onChange={(e) => setField("first_name", e.target.value)}
                          placeholder="John"
                        />
                        {errorFor("first_name") && (
                          <p className="text-sm text-red-600">{errorFor("first_name")}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input
                          id="last_name"
                          value={form.data.last_name}
                          onChange={(e) => setField("last_name", e.target.value)}
                          placeholder="Smith"
                        />
                        {errorFor("last_name") && (
                          <p className="text-sm text-red-600">{errorFor("last_name")}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={form.data.email}
                          onChange={(e) => setField("email", e.target.value)}
                          placeholder="you@example.com"
                        />
                        {errorFor("email") && (
                          <p className="text-sm text-red-600">{errorFor("email")}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={form.data.phone}
                          onChange={handlePhoneChange}
                          placeholder="(555) 123-4567"
                        />
                        {errorFor("phone") && (
                          <p className="text-sm text-red-600">{errorFor("phone")}</p>
                        )}
                      </div>
                    </div>

                    {/* Your Business */}
                    <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">
                      Your Business
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input
                          id="company_name"
                          value={form.data.company_name}
                          onChange={(e) => setField("company_name", e.target.value)}
                          placeholder="Your company"
                        />
                        {errorFor("company_name") && (
                          <p className="text-sm text-red-600">{errorFor("company_name")}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          type="url"
                          value={form.data.website}
                          onChange={(e) => setField("website", e.target.value)}
                          placeholder="https://yourcompany.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="title_role">Title / Role *</Label>
                        <Input
                          id="title_role"
                          value={form.data.title_role}
                          onChange={(e) => setField("title_role", e.target.value)}
                          placeholder="CEO, Founder, etc."
                        />
                        {errorFor("title_role") && (
                          <p className="text-sm text-red-600">{errorFor("title_role")}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="linkedin">LinkedIn Profile</Label>
                        <Input
                          id="linkedin"
                          type="url"
                          value={form.data.linkedin}
                          onChange={(e) => setField("linkedin", e.target.value)}
                          placeholder="https://linkedin.com/in/yourname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country *</Label>
                        <select
                          id="country"
                          value={form.data.country}
                          onChange={(e) => {
                            const newCountry = e.target.value
                            if (newCountry !== form.data.country) {
                              form.setData((prev) => ({ ...prev, country: newCountry, state: "" }))
                            }
                            if (clientErrors.country) setClientErrors((prev) => ({ ...prev, country: undefined }))
                            if (clientErrors.state) setClientErrors((prev) => ({ ...prev, state: undefined }))
                          }}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={form.data.city}
                          onChange={(e) => setField("city", e.target.value)}
                          placeholder="Your city"
                        />
                        {errorFor("city") && (
                          <p className="text-sm text-red-600">{errorFor("city")}</p>
                        )}
                      </div>
                      {form.data.country === "US" ? (
                        <div className="space-y-2">
                          <Label htmlFor="state">State *</Label>
                          <Select
                            value={form.data.state}
                            onValueChange={(value) => setField("state", value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select a state" />
                            </SelectTrigger>
                            <SelectContent>
                              {US_STATES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errorFor("state") && (
                            <p className="text-sm text-red-600">{errorFor("state")}</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label htmlFor="state">State / Province</Label>
                          <Input
                            id="state"
                            value={form.data.state}
                            onChange={(e) => setField("state", e.target.value)}
                            placeholder="State or province"
                          />
                        </div>
                      )}
                    </div>

                    {/* Short Reflection */}
                    <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">
                      Short Reflection
                    </h3>
                    <div className="space-y-2 mb-8">
                      <Label htmlFor="faith_reflection">
                        In a few sentences, how has your faith influenced the way you lead your business? *
                      </Label>
                      <Textarea
                        id="faith_reflection"
                        value={form.data.faith_reflection}
                        onChange={(e) => setField("faith_reflection", e.target.value)}
                        placeholder="Share a little about how your faith shapes your leadership and business decisions..."
                        rows={5}
                        maxLength={2000}
                      />
                      <div className="flex justify-between items-center">
                        {errorFor("faith_reflection") ? (
                          <p className="text-sm text-red-600">{errorFor("faith_reflection")}</p>
                        ) : (
                          <span />
                        )}
                        <span className="text-xs text-zinc-400">
                          {form.data.faith_reflection.length} / 2000
                        </span>
                      </div>
                    </div>

                    {/* Optional Upload */}
                    <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">
                      Optional Upload
                    </h3>
                    <div className="space-y-2 mb-8">
                      <Label>Headshot Photo</Label>
                      <p className="text-sm text-zinc-500 mb-2">
                        Upload a professional headshot, or we&apos;ll use one from your LinkedIn or company website. JPEG, PNG, or WebP under 5MB.
                      </p>
                      <div className="flex items-center gap-4">
                        {headshotPreview && (
                          <img
                            src={headshotPreview}
                            alt="Headshot preview"
                            className="w-16 h-16 rounded-full object-cover border border-zinc-200"
                          />
                        )}
                        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-300 bg-white text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Upload className="h-4 w-4" />
                          {headshotPreview ? "Change Photo" : "Choose File"}
                          <input
                            type="file"
                            className="sr-only"
                            accept=".jpg,.jpeg,.png,.webp"
                            onChange={handleHeadshotChange}
                          />
                        </label>
                      </div>
                      {errorFor("headshot") && (
                        <p className="text-sm text-red-600">{errorFor("headshot")}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="gradient"
                      size="lg"
                      disabled={form.processing}
                      className="w-full sm:w-auto"
                    >
                      {form.processing ? "Submitting..." : "Submit Application"}
                    </Button>
                  </form>
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-kf-red rounded-lg p-8 text-white sticky top-32">
                    <h3 className="font-display text-2xl font-semibold mb-4">
                      Why Your Story Matters
                    </h3>
                    <p className="text-red-100 leading-relaxed mb-4">
                      Every featured leader started exactly where you are &mdash; wondering if their story was worth sharing. It is. The Kingdom Factor Collective exists because real, honest stories from real business owners change lives.
                    </p>
                    <p className="text-red-100 leading-relaxed mb-6">
                      Your faith-driven leadership journey could be the exact encouragement another Christian entrepreneur needs to keep going, lead boldly, and trust God in their business.
                    </p>
                    <div className="bg-white/15 rounded-lg p-4 flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-red-200 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-100">
                        Applications are reviewed personally by our team. We&apos;ll be in touch shortly after you submit.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Section 7: Final CTA */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl text-kf-red">
              Your Story Could Encourage the Next Christian Leader
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
              Many Christian business owners feel alone navigating faith and leadership in the marketplace. They wonder if it&apos;s really possible to honor God in their industry, to make decisions from biblical values, and to lead with integrity when the world pressures them otherwise.
            </p>
            <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
              Your story &mdash; your journey, your faith, your wins and your hard lessons &mdash; might be the very thing someone else needs to hear. One published story can reach hundreds of leaders. One honest answer can unlock courage in someone who&apos;s been waiting for permission to lead differently.
            </p>
            <blockquote className="mt-8 text-lg text-zinc-700 border-l-4 border-kf-red pl-6 text-left max-w-xl mx-auto italic">
              You don&apos;t need a perfect story. You just need a <strong className="not-italic">faithful</strong> one.
            </blockquote>
            <Button variant="gradient" size="lg" className="mt-8" onClick={scrollToForm}>
              Apply to Be Featured
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        <Footer activePage="share-your-story" />
      </div>
    </>
  )
}
